import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, Avatar, Button, Spin, Typography, Space } from "antd";
import { TeamOutlined, UserOutlined } from "@ant-design/icons";
import Axios from "axios";
import "./GroupPreviewPage.css";

const { Title, Text } = Typography;

interface GroupInfo {
  _id: string;
  chatTitle?: string;
  title?: string;
  avatar?: string;
  type?: "direct" | "group" | "ai";
  users?: any[];
  memberCount?: number;
}

const GroupPreviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [groupInfo, setGroupInfo] = useState<GroupInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGroupInfo = async () => {
      if (!id) {
        setError("Invalid group ID");
        setLoading(false);
        return;
      }

      try {
        const response = await Axios.get(`/api/chats/public/${id}`);
        if (response.data?.data) {
          const chat = response.data.data;
          if (chat.type === "group") {
            setGroupInfo(chat);
          } else {
            setError("This is not a group chat");
          }
        } else {
          setError("Group not found");
        }
      } catch (err: any) {
        if (err.response?.status === 401) {
          setError("Group not found or access denied");
        } else if (err.response?.status === 404) {
          setError("Group not found");
        } else {
          setError("Failed to load group information");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchGroupInfo();
  }, [id]);

  const handleJoinClick = () => {
    if (id) {
      window.location.href = `lintalk://group/${id}`;
    }
  };

  const getDisplayName = () => {
    if (!groupInfo) return "";
    return groupInfo.chatTitle || groupInfo.title || "Untitled Group";
  };

  const getMemberCount = () => {
    if (!groupInfo) return 0;
    if (groupInfo.memberCount !== undefined) {
      return groupInfo.memberCount;
    }
    if (groupInfo.users && Array.isArray(groupInfo.users)) {
      return groupInfo.users.length;
    }
    return 0;
  };

  const getAvatarUrl = () => {
    if (!groupInfo?.avatar) return null;
    if (groupInfo.avatar.startsWith("http")) {
      return groupInfo.avatar;
    }
    return `/api/files/${groupInfo.avatar}`;
  };

  if (loading) {
    return (
      <div className="group-preview-container">
        <div className="group-preview-center">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (error || !groupInfo) {
    return (
      <div className="group-preview-container">
        <div className="group-preview-center">
          <Card className="group-preview-card">
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Title level={4} type="danger">
                {error || "Group not found"}
              </Title>
              <Text type="secondary">
                The group you're looking for doesn't exist or is no longer
                available.
              </Text>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="group-preview-container">
      <div className="group-preview-center">
        <Card className="group-preview-card" bordered={false}>
          <div className="group-preview-content">
            <Avatar
              size={80}
              src={getAvatarUrl()}
              icon={<TeamOutlined />}
              className="group-avatar"
            />
            <Title level={3} className="group-title">
              {getDisplayName()}
            </Title>
            <Space className="group-meta" size="middle">
              <Space size="small">
                <UserOutlined />
                <Text type="secondary">{getMemberCount()} members</Text>
              </Space>
            </Space>
            <Button
              type="primary"
              size="large"
              block
              onClick={handleJoinClick}
              className="join-button"
            >
              Join Group
            </Button>
            <Text type="secondary" className="join-hint">
              Opens in Lingua Talk app
            </Text>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default GroupPreviewPage;
